import React, { useState, lazy, Suspense } from "react";
import { Text } from "react-native";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { COLORS } from "../../constants/colors";
import { useRoute } from "@react-navigation/native";
import { getTrailDataById } from "../../utils/fetch";
import { parseTrailData } from "../../utils/trails";
import { logAnalyticsEvent } from "../../utils/firebase";
import SnackbarMessage from "../../components/Snackbar/SnackbarMessage";

const DetailsCommon = lazy(() => import("./DetailsCommon"));
const MapViewDetailWeb = lazy(() => import("./MapViewDetail.web"));

/**
 * DetailsViewWeb component is responsible for rendering the details view for a trail in a web environment.
 * It receives the detail object as a route parameter and passes it along to the DetailsCommon component for rendering.
 */
const DetailsView = () => {
  logAnalyticsEvent("screen_view", { name: "Trails-DetailView" });

  const route = useRoute();

  const [trailData, setTrailData] = useState([]);
  const [trailLat, setTrailLat] = useState([]);
  const [trailLng, setTrailLng] = useState([]);
  const [trailPolyline, setTrailPolyline] = useState([]);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  /**
   * Fetches trail data based on the route parameters.
   *
   * This function first attempts to retrieve trail data from local storage.
   * If the trail data is not found locally, it fetches the data from an external source using the trail ID.
   * The retrieved trail data is then parsed and used to update the state with trail attributes, latitude, longitude, and polyline.
   *
   * @async
   * @function getTrailData
   * @returns {Promise<void>} A promise that resolves when the trail data has been fetched and state has been updated.
   */
  const getTrailData = async () => {
    try {
      const getTrail = await getTrailDataById(route?.params?.id);
      const getTrailData = await parseTrailData(getTrail);
      setTrailData(getTrailData[0].attributes);
      setTrailLat(parseFloat(getTrailData[0].lat));
      setTrailLng(parseFloat(getTrailData[0].lng));
      setTrailPolyline(getTrailData[0].polyline);
      //throw new Error("No trail data found");
    } catch (error) {
      setSnackbarMessage("Error getting trail data. Please try again.");
      setSnackbarVisible(true);
    }
  };

  React.useEffect(() => {
    getTrailData();
  }, []);

  return (
    <div style={styles.container}>
      <Suspense fallback={<Text>Loading...</Text>}>
        <SnackbarMessage visible={snackbarVisible} message={snackbarMessage} />
        {!snackbarVisible && (
          <DetailsCommon
            FontAwesomeIcon={FontAwesomeIcon}
            detail={trailData}
            lat={trailLat}
            lng={trailLng}
            polyline={trailPolyline}
            MapComponent={MapViewDetailWeb}
          />
        )}
      </Suspense>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: COLORS.backgroundColor,
  },
};

export default DetailsView;
